import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { cpPurchaseDomain, cpGetPurchaseStatus, cpRetryPurchase } from '@/lib/api/controlPlaneClient'
import { toast } from 'sonner'

const SESSION_STORAGE_KEY = 'domain-purchase-id'
const POLL_INTERVAL = 3000

function getStoredPurchaseId(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY)
  } catch {
    return null
  }
}

export function useDomainPurchase() {
  const queryClient = useQueryClient()
  const [purchaseId, setPurchaseId] = useState<string | null>(getStoredPurchaseId)

  // Sync purchaseId to sessionStorage
  useEffect(() => {
    try {
      if (purchaseId) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, purchaseId)
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY)
      }
    } catch {
      // sessionStorage unavailable
    }
  }, [purchaseId])

  // Purchase mutation
  const purchaseMutation = useMutation({
    mutationFn: (domain: string) => cpPurchaseDomain(domain),
    onSuccess: (data) => {
      setPurchaseId(data.purchase_id)
    },
  })

  // Status polling
  const statusQuery = useQuery({
    queryKey: ['domain-purchase-status', purchaseId],
    queryFn: () => cpGetPurchaseStatus(purchaseId!),
    enabled: !!purchaseId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return POLL_INTERVAL
      if (data.is_complete || data.stage === 'failed') return false
      return POLL_INTERVAL
    },
    placeholderData: keepPreviousData,
  })

  const isComplete = statusQuery.data?.is_complete === true
  const isFailed = statusQuery.data?.stage === 'failed'

  // On completion: invalidate domain status, show toast
  useEffect(() => {
    if (isComplete) {
      queryClient.invalidateQueries({ queryKey: ['custom-domain-status'] })
      toast.success('Your domain is live!')
    }
  }, [isComplete, queryClient])

  // Retry mutation
  const retryMutation = useMutation({
    mutationFn: () => cpRetryPurchase(purchaseId!),
    onSuccess: (data) => {
      setPurchaseId(data.purchase_id)
      queryClient.invalidateQueries({ queryKey: ['domain-purchase-status', purchaseId] })
    },
  })

  const reset = () => {
    setPurchaseId(null)
    purchaseMutation.reset()
    retryMutation.reset()
  }

  return {
    // Purchase action
    purchase: purchaseMutation.mutate,
    isPurchasing: purchaseMutation.isPending,
    purchaseError: purchaseMutation.error?.message ?? null,

    // Provisioning status
    purchaseId,
    domain: statusQuery.data?.domain ?? null,
    stage: statusQuery.data?.stage ?? null,
    stageLabel: statusQuery.data?.stage_label ?? null,
    isComplete,
    isFailed,
    error: statusQuery.data?.error ?? null,

    // Retry
    retry: retryMutation.mutate,
    isRetrying: retryMutation.isPending,

    // Reset
    reset,
  }
}
